/* eslint-disable camelcase */
import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { folderCreate, setFolderData } from "~/util/providers/mapybox";
import { getMapyboxError, mapyboxNotification } from "~/util/mapybox";
import { node } from "~/util/dom";
import { gpxLog } from "~/gpx/util";
import { save } from "~/es6/provider-gpx";
import MyMapsFolderSelectCreate from "../mymaps/folder-select-create";

type TDuplicity = "check" | "ignore";

interface IGPXSave extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	saveData: any;
	folder?: {
		id: string;
		title: string;
	};
	onSave?: (folderId: string, folderTitle: string) => void;
	onError?: () => void;
}

export default class GPXSave {
	_opts: IGPXSave;

	_dialog: MyMapsDialog;

	_saveData: any;

	_duplicity: TDuplicity;

	_folderSelectCreate: MyMapsFolderSelectCreate;

	constructor(data: IGPXSave) {
		this._opts = {
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onError: () => {},
			...data,
		};
		this._saveData = this._opts.saveData;
		this._duplicity = "check";

		this._build();
	}

	open() {
		this._dialog?.open();
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: [_("saveToFolder")],
				saveBtn: _("save"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayTest: () => true,
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const mainContent = dialog.contents[0];

		mainContent.classList.add("mymaps-dialog__defContent");
		dialog.dom.container.classList.add("mymaps__gpxSave");

		this._folderSelectCreate = new MyMapsFolderSelectCreate({
			folder: this._opts.folder,
			onChange: state => {
				this._dialog.setSaveBtnTitle = state.createNewFolder ? _("mymap.folders.createAndSave") : _("save");
			},
		});

		// slozka
		mainContent.append(this._folderSelectCreate.container);

		this._dialog = dialog;
	}

	async _onSave() {
		const folderState = this._folderSelectCreate.state;
		let folderId = folderState.folderId;

		this._dialogLoading(true);

		// nova slozka
		if (folderState.createNewFolder) {
			try {
				if (folderState.newFolderTitle === "") {
					this._dialog.loading = false;
					this._dialog.error = _("mymap.folders.emptyFolder");

					return;
				}

				const createData = await folderCreate(folderState.newFolderTitle);

				folderId = createData.id;
				// ulozime - pro pripad override
				this._folderSelectCreate.overwriteOrigState(folderId);
			} catch (exc) {
				this._dialog.error = getMapyboxError({
					operation: "folder-create",
				});
				this._dialog.loading = false;

				return;
			}
		}

		const savedData = await this._save(folderId);

		this._dialogLoading(false);

		if (savedData.closeDialog) {
			setFolderData({
				id: folderId,
				title: folderState.folderTitle,
			});

			// notifikace
			mapyboxNotification({
				operation: "gpx-import",
				folderId,
				folderTitle: folderState.folderTitle,
			});

			this._opts.onSave(folderId, folderState.folderTitle);
			this._closeDialog(true);
		}
	}

	_dialogLoading(state: boolean) {
		if (state) {
			this._dialog.error = "";
		}

		this._dialog.loading = state;
		this._dialog.dom.buttons.classList[state ? "add" : "remove"]("btns-loading");
	}

	async _save(folderId: string): Promise<{ closeDialog: boolean; }> {
		let closeDialog = false;

		// ulozime
		try {
			const data = await save(this._saveData, folderId, this._duplicity);

			// chyba
			if (data.error) {
				this._dialog.error = _("gpx.import.saveError");
			// konflikt
			} else if (data.conflict) {
				// nastavime
				this._duplicity = "ignore";
				this._saveData = data.new;

				this._dialog.error = _("gpx.import.saveDuplicateError", {
					NUM: data.duplicityCount,
				});

				this._dialog.setSaveBtnTitle = _("saveOnlyNew");

				const saveAllBtn = node("button", { className: "mymaps-dialog__cancelBtn", type: "button", textContent: _("saveAll") });

				saveAllBtn.addEventListener("click", () => {
					this._saveData = data.items;

					// znovu ulozime, budou duplicity
					this._onSave();
				});
				this._dialog.dom.saveBtn.after(saveAllBtn);
			// jinak se zavri
			} else {
				gpxLog("click", {
					text: "Uložit",
					type: "import",
					origin: "web",
					files: {
						num_points: this._saveData.filter(item => item.type === "point").length,
						num_paths: this._saveData.filter(item => item.type === "path").length,
						// protoze route neni route ale path/track
						num_routes: 0,
						num_activities: this._saveData.filter(item => item.type === "track").length,
					},
				});
				closeDialog = true;
			}
		} catch (exc) {
			this._dialog.error = getMapyboxError({
				operation: "gpx-import",
			});
		}

		return {
			closeDialog,
		};
	}
}
